// Voice Intelligence — conversation RPC + sentence layer.
//
// Built per the Voice Intelligence Build Spec (Part A.1). Single data-access
// surface for the Voice (and, later, Chat) Intelligence analysis band and
// detail pane. Calls the 12 deployed Supabase RPCs plus a direct
// `conversation_sentences` table read.
//
// Design (per the frontend plan, D-5):
//   - Import the singleton client from `../lib/supabase` (no `src/api/client.js`
//     in this repo).
//   - Raw return + throw (the spec's `conversationRpc`), NOT the legacy
//     `{error, data, message}` envelope. Call sites use `Promise.all` with a
//     per-promise `.catch` (the CdpDashboardPage idiom) so one failure never
//     blocks the batch.
//   - Every call checks `data?.error` and throws — the spec's "every RPC gates
//     itself" contract the repo's other helpers do NOT do.
//   - Errors are labelled with the RPC name (the lesson at api/auth.js:11–14).
//
// Phase 0 is a hard gate: probe live data to confirm every RPC is deployed and
// the return shapes match the documented contracts BEFORE relying on any
// component. The wrappers below are built to those documented contracts.

import { supabase } from '../lib/supabase';

// ─── Core RPC helper ────────────────────────────────────────────────────────
// Calls a Supabase RPC, throws a labelled error on failure (Supabase error OR
// the RPC's own `{error:true, message}` envelope), returns raw `data`.
// A refused HTTP/2 stream is a transport failure, not a database one. This page
// opens ~60 requests on load (the band's batch, the list's paging, profile and
// feature lookups), and past that ceiling the server refuses streams outright:
// `net::ERR_HTTP2_SERVER_REFUSED_STREAM`, which supabase-js surfaces as a fetch
// failure. Whole panels then render "No data for this period" over data that
// exists — the trend chart lost this race on every load.
//
// Retried because these are idempotent reads and the next attempt lands after
// the burst has drained. Deliberately NOT routed through auth.js's
// `rpcReadWithRetry`: that one is scoped to Postgres 57014 for AIM/RI, and its
// own comment records that widening it once already changed five unrelated
// modules by accident. A Postgres error, an RLS refusal or a bad parameter still
// fails on the first attempt, where it belongs.
const TRANSPORT_FAILURE = /failed to fetch|networkerror|load failed|err_http2|connection closed/i;
const RPC_RETRY_DELAYS_MS = [200, 600];
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isTransportFailure = (err) =>
  !err?.code && TRANSPORT_FAILURE.test(err?.message || '');

// ─── Concurrency gate ───────────────────────────────────────────────────────
// The refused streams are congestion, and retrying congestion in parallel makes
// it worse: one measured load fired 88 requests, 38 of them failed, and the
// per-RPC counts showed the retries themselves colliding (six attempts at a
// single function). Capping how many of THIS module's reads are in flight fixes
// the cause instead of absorbing the symptom.
//
// The page-load batch is still one Promise.all fired together — the spec's
// requirement — it simply drains four at a time instead of eight at once, which
// costs nothing perceptible and leaves headroom for the app shell's own calls.
const MAX_IN_FLIGHT = 4;
let inFlight = 0;
const waiting = [];

const acquire = () =>
  new Promise((resolve) => {
    if (inFlight < MAX_IN_FLIGHT) { inFlight += 1; resolve(); return; }
    waiting.push(resolve);
  });

const release = () => {
  const next = waiting.shift();
  if (next) { next(); return; }   // hand the slot straight on
  inFlight -= 1;
};

// Jitter so a batch that fails together does not retry together — synchronised
// retries reproduce the exact burst that caused the failure.
const jitter = (ms) => ms + Math.floor(Math.random() * 250);

export async function conversationRpc(name, params, label, signal) {
  // try/catch, not `.catch()` on the builder: supabase.rpc() returns a thenable
  // PostgrestFilterBuilder, which has `then` but no `catch`. Calling .catch on it
  // throws before the request is ever issued — the panel then renders its empty
  // state having made no network call at all.
  //
  // `signal` (optional AbortSignal) is forwarded to the underlying fetch via
  // postgrest-js's `.abortSignal()` so an aborted caller cancels the in-flight
  // request rather than only ignoring its result. Callers that omit it are
  // unchanged. postgrest-js re-throws an aborted fetch as an AbortError; we
  // re-throw it with name 'AbortError' (below) so callers can distinguish a
  // deliberate cancel from a genuine failure — see LocalIntelligencePage's
  // StrictMode double-mount cancel (work-page-fixes Item 1).
  const attempt = async () => {
    await acquire();
    try {
      const builder = supabase.rpc(name, params);
      if (signal) builder.abortSignal(signal);
      return await builder;
    } catch (e) {
      return { data: null, error: e };
    } finally {
      release();
    }
  };
  let result = await attempt();
  for (const ms of RPC_RETRY_DELAYS_MS) {
    if (!isTransportFailure(result.error)) break;
    // eslint-disable-next-line no-await-in-loop
    await sleep(jitter(ms));
    // eslint-disable-next-line no-await-in-loop
    result = await attempt();
  }
  const { data, error } = result;
  if (error) {
    // An aborted request is intentional, not a failure — re-throw it as an
    // AbortError (name === 'AbortError') so the caller's catch can tell it apart
    // from a genuine RPC error. Don't wrap it into a labelled Error, or the
    // page's AbortError guard would never match.
    if (error?.name === 'AbortError' || error?.code === 'ABORT_ERR') {
      const abortErr = new Error(error?.message || 'The user aborted a request.');
      abortErr.name = 'AbortError';
      throw abortErr;
    }
    const detail = error?.message || 'Supabase RPC failed';
    const message = label ? `${label}: ${detail}` : detail;
    throw new Error(message);
  }
  // The spec: every RPC gates itself and returns {error:true, message} rather
  // than throwing. Surface that as a real error so the per-promise .catch path
  // fires and PanelErrorBoundary renders a friendly message.
  if (data && typeof data === 'object' && data.error === true) {
    const detail = data.message || `${name} returned an error`;
    const message = label ? `${label}: ${detail}` : detail;
    throw new Error(message);
  }
  return data;
}

// ─── Sentence layer ────────────────────────────────────────────────────────
// Reads `conversation_sentences` (a direct table read, not an RPC — same shape
// as `getGuestIntelSentences` against `guest_intel_sentences`). Returns a flat
// { [panel_key]: sentence } map. Sentences are generated by a scheduled backend
// job; custom/arbitrary date ranges will have none. Progressive enhancement —
// never block on it.
//
// NEVER throws — mirrors `getGuestIntelSentences`'s never-throw-on-RLS
// behaviour. The page's `.then(r => setSay(r ?? {}))` is safe without a .catch.
// `dateRange` = `${rangeStart}_${rangeEnd}` from ViewContext's UTC boundaries
// (contexts/ViewContext.js:487) so it matches generated rows. Do not recompute
// dates locally.
// Sentences come from fn_get_conversation_sentences, NOT from a direct read of
// conversation_sentences. The 2026-08-25 addendum retired the table query: rows
// were keyed on a range computed from the day the job ran, so eight days of runs
// produced eight "last 7 days" rows per client and none matched what the picker
// would ask for. `fn_canonical_range` now defines all six ranges with the same
// inclusive arithmetic the picker uses.
//
// Returns { sentences, served, exact } rather than a bare map, because a
// near-miss has to be declarable:
//   served: null            no sentences for this range — the D.2 no-sentence
//                           state, render nothing and leave no gap
//   exact: true             the stored range IS the asked-for one
//   exact: false + served   a neighbouring window within three days, whose span
//                           is within a day. The caller MUST name the period it
//                           actually covers; attributing a claim to a period it
//                           was not measured over is the thing this shape exists
//                           to prevent.
//
// Still never throws. Both bands treat sentences as progressive enhancement and
// run them inside a Promise.all whose other members carry the page — a rejected
// read here would take panels down that have their own data.
export async function conversationSentences(clientId, startDate, endDate) {
  const { data, error } = await supabase.rpc('fn_get_conversation_sentences', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error || data?.error) {
    // eslint-disable-next-line no-console
    console.warn('[ConversationIntel] sentences read failed:', error?.message || data?.message);
    return { sentences: {}, served: null, exact: false };
  }
  return {
    sentences: data?.sentences || {},
    served: data?.served || null,
    // Only meaningful alongside a served range; false with served:null is just
    // "nothing stored", not a near-miss to disclose.
    exact: data?.exact === true,
  };
}

// ─── Page-load RPCs (the first seven + sentences) ──────────────────────────
// These feed the analysis band tiers. Each documents its params + return keys
// so call sites stay narrow at the boundary. All take p_client_id first.

// fn_get_conversation_summary — {p_client_id, p_start_date, p_end_date}
// Returns {handled, outcomes, bookings, leads, demand, attribution, website_gaps}.
// Feeds: voice_verdict (Tier 1), voice_after_hours/voice_recovery (Tier 2),
// voice_demand/voice_barriers (Tier 3), voice_attribution (Tier 4).
// p_modality added 2026-08-27 (addendum 2 §4). Before it, `handled` came back
// {chat: 66, voice: 100} behind panels labelled as chat — 60% of the data under
// chat_demand, chat_barriers and chat_attribution was phone calls. NULL keeps the
// old cross-channel behaviour, so the two pages can deploy independently.
export const getConversationSummary = (clientId, startDate, endDate, modality = null) =>
  conversationRpc('fn_get_conversation_summary',
    { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate, p_modality: modality },
    'conversation summary');

// fn_get_conversation_bookings — {p_client_id, p_start_date, p_end_date, p_modality}
// Returns FOUR separate, honest numbers plus the note (Addendum 1 §5 — the
// `confirmed: 0` "phone bookings are invisible" shape is gone):
//   agent_booked                {count, revenue, means, drill:{what:'reservation', id:null}}
//     — reservations the agent built and the guest paid for.
//   agent_influenced            {count, revenue, means, drill:{what:'outcome', id:'reservation_made'}}
//     — the guest finished the booking themselves after speaking to the agent.
//   in_progress                 {count, value, means}
//     — a reservation was built and not paid for; recoverable, not lost.
//   existing_booking_enquiries  {count, answered_without_staff, means,
//                                drill:{what:'outcome', id:'reservation_assisted'}}
//     — callers asking about a booking they already had. Service, never revenue,
//       and never described as a change (the agent cannot alter a reservation).
//   note                        — per-modality front-desk caveat, rendered verbatim.
// Feeds: voice_bookings / chat_bookings (Tier 2 — the four-cell money strip).
// NEVER sum these into one "bookings" total; 46 and 19 are both defensible, 65 is not.
// The old fields (confirmed, revenue, agent_reported_unconfirmed, about_existing,
// revenue_source) are gone for good — do not reference them.
export const getConversationBookings = (clientId, startDate, endDate, modality = 'voice') =>
  conversationRpc('fn_get_conversation_bookings',
    { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate, p_modality: modality },
    'conversation bookings');

// fn_get_inquiry_funnel — {p_client_id, p_start_date, p_end_date, p_modality}
// Returns {stages[], metrics{QDR, CCR, GAR, conversion}, bookings, recoverable}.
// Voice includes GAR (greeting answer rate); chat does not. Feeds: voice_funnel
// (Tier 3 — full width, the most valuable panel). Render stages[] as given —
// do NOT merge stages frontend-side (Part F forbids computing).
export const getInquiryFunnel = (clientId, startDate, endDate, modality = 'voice') =>
  conversationRpc('fn_get_inquiry_funnel',
    { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate, p_modality: modality },
    'inquiry funnel');

// fn_get_call_loss_histogram — {p_client_id, p_start_date, p_end_date}
// Returns {buckets[]} with pct_never_spoke overlaid + two averages.
// Voice-only (no greeting to abandon on chat). Feeds: voice_greeting_loss.
export const getCallLossHistogram = (clientId, startDate, endDate) =>
  conversationRpc('fn_get_call_loss_histogram',
    { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate },
    'call loss histogram');

// fn_get_conversation_followups — {p_client_id, p_days, p_limit, p_modality}
// Returns {items[], window_days} — reason, who, what they wanted, why not, value.
// A worklist that empties. Feeds voice_followups and chat_followups (Tier 3).
//
// It was NOT modality-scoped until 2026-08-27; p_modality returned PGRST202 and
// the panel filtered client-side instead. Re-probed 2026-08-27: accepted.
// p_modality added 2026-08-27 (addendum 2 §4). This is what put voice rows —
// with phone numbers — on a chat page whose spec says there are none. The panel
// filtered them client-side as a stopgap; that filter is now removed.
export const getConversationFollowups = (clientId, { days = 7, limit = 50, modality = null } = {}) =>
  conversationRpc('fn_get_conversation_followups',
    { p_client_id: clientId, p_days: days, p_limit: limit, p_modality: modality },
    'conversation followups');

// fn_get_conversation_trend — {p_client_id, p_start_date, p_end_date, p_modality}
// Returns {bucket, series[]}. Feeds: the full-width trend chart (Tier 2).
export const getConversationTrend = (clientId, startDate, endDate, modality = 'voice') =>
  conversationRpc('fn_get_conversation_trend',
    { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate, p_modality: modality },
    'conversation trend');

// fn_get_agent_value — {p_client_id, p_start_date, p_end_date, p_hourly_rate,
// p_modality}. Returns {workload, time_saved, recoverable, guest_service,
// after_hours, assumptions}. Feeds: voice_time_saved / chat_time_saved
// (Tier 4 — editable hourly rate; re-fire with p_hourly_rate on change, NEVER
// multiply client-side), workload (Tier 4), and the after-hours money-strip cell
// (Tier 2, Phone). Use only workload/time_saved/recoverable/guest_service/
// after_hours here; booking numbers come from getConversationBookings.
//
// THREE THINGS CHANGED SHAPE (Addendum 1 §5b):
//   - The `bookings` sub-object is GONE. It duplicated fn_get_conversation_bookings
//     and the two would have drifted; that function is the only source of booking
//     numbers now. Do not read agent_value.bookings — it no longer exists.
//   - time_saved is now two channels with two bases:
//       {hours, labour_value, conversations, hourly_rate,
//        voice:{hours, conversations, basis},
//        chat:{hours, conversations, visitor_messages, basis},
//        excluded:{transferred}, note}
//     Render both channel figures, both basis strings, and the excluded note —
//     chat cannot use elapsed time (its median session is a 24h timeout), so it is
//     priced from visitor messages and the basis says so.
//   - workload gained handled_without_a_person (pre-computed — use it, never derive
//     a rate from the total) and a corrected self_resolution_rate that now differs
//     sharply by channel (74.2 Phone / 98.3 Chat). Render it per page, NEVER combined.
//
// MODALITY-SCOPED as of V6 (spec A.5.3, and the v5→v6 changelog: "fn_get_agent_value
// was not modality-scoped — a Phone panel showed 55 after-hours calls when Phone
// alone was 31. p_modality added. Verified: Phone returns 0 chat conversations").
// Pass p_modality or the Phone page shows chat figures. NULL keeps the old
// cross-channel behaviour so a caller that does not scope still gets what it always
// got. (E.10's "cross-modality" note is about the user-facing label "your agents",
// not about omitting this parameter.)
export const getAgentValue = (clientId, startDate, endDate, hourlyRate = null, modality = null) => {
  const params = { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate, p_modality: modality };
  if (hourlyRate != null) params.p_hourly_rate = hourlyRate;
  return conversationRpc('fn_get_agent_value', params, 'agent value');
};

// ─── On-demand RPCs ─────────────────────────────────────────────────────────

// fn_get_knowledge_gaps — {p_client_id, p_status, p_min_occurrences, p_limit, p_modality}
// Returns {items[]} — topic, raw_topic, variants, gap_type, occurrences,
// incorrect_occurrences, asked_on_chat, asked_on_voice, agent_said,
// correct_answer, example_questions, example_conversations, priority,
// verification, verification_means. Feeds the website-gaps panel on BOTH channels.
// incorrect_information sorts to the top regardless of count.
//
// verification (Addendum 1 §5c) says how the agent_said claim was captured:
//   'exact'          — chat text stored verbatim as the visitor sent it; trustworthy.
//   'from_transcript' — read from a call transcript by speech recognition, which is
//                       confidently wrong on proper nouns and numbers — the very
//                       things these claims are made of. NEVER present agent_said as
//                       a stated fact on these; render verification_means as the
//                       caveat beside the claim. Do NOT filter them out — they are
//                       real signals, just not proof.
//   'mixed'          — some of each; treat as 'from_transcript'.
// verification_means is the human-readable caveat string to render on a
// non-exact row.
//
// Rows are CLUSTERED as of 2026-08-27 (addendum 2 §6). Verde's 622 open gaps
// were mostly singletons because the harvester made a row per phrasing —
// "check-in time" existed nine ways. They now group by canonical topic into ~20
// real ones. `gap_type` is also no longer bool_or: one wrong answer in 64 used to
// flag a whole group as incorrect_information, so 17 of 20 rows read urgent and
// the priority signal was worthless. It is now the type accounting for the most
// occurrences, with `incorrect_occurrences` exposed separately — a topic can
// honestly read knowledge_gap with sixteen wrong answers inside it.
//
// p_client_id is REQUIRED — without it PostgREST finds no matching overload and
// the panel renders empty.
//
// p_status, p_min_occurrences and p_limit are ALL sent as of Build Spec v2 §A1.
// This REVERSES addendum 2 §8, which had us stop sending p_status after be439fb;
// spec v2 supersedes that addendum, so the filter is back and is now the default
// rather than something a caller opts into.
//
// THE BEHAVIOUR CHANGE THIS BUYS, STATED PLAINLY: a gap that has been resolved
// disappears from the panel. Before this, the panel showed every gap ever
// harvested and a reader could not tell a live gap from one already fixed. Now
// the panel is a worklist that empties — which is the point — at the cost that
// there is no longer any way to see what was closed. That is deliberate and
// visible, not an accident of parameter passing.
//
// p_min_occurrences: 1 is the floor, not a filter — it admits a topic asked
// exactly once, which is what the sort's incorrect_information pin depends on.
// Sending it explicitly pins the contract rather than trusting the function's
// own default to stay at 1.
//
// EVIDENCE, AND THE BLAST RADIUS OF p_status.
//
// The first probe behind these parameters ran at p_limit=5 and returned 200 with
// 5 items, which proves only that at least five open rows exist — it says nothing
// about how many rows `p_status:'open'` REMOVES, and the panel below fetches 50.
// Re-probed at limit 500 on 2026-08-28 (Verde Ranch, platform admin): filtered
// and unfiltered return the identical set — 108 chat gaps and 215 voice gaps,
// zero rows dropped. The rows do not even carry a `status` field in the response.
// So the filter changes nothing at this client today, and WebsiteGapsPanel's
// 50-row population is unaffected.
//
// ONE CLIENT, ONE DAY. A property that has actually resolved gaps would see rows
// disappear, which remains the intended behaviour change — it simply has not been
// observed anywhere yet. And this is an observation of the function AS DEPLOYED
// ON THAT DATE: a redeploy changing the overload set invalidates it with nothing
// in this repo changing, symptom PGRST202 and an empty panel. Re-probe rather
// than reasoning from this comment.
//
// p_modality added 2026-08-27 (§4). kb_gap_candidates has no modality column and
// should not — the same question genuinely arrives on both channels — so the
// backend derives it from example_conversations. Verde on 2026-08-28: 108 chat
// gaps, 215 voice. (The 622/191/438 figures this comment used to carry were from
// 2026-08-27, before the clustering landed; they are not comparable and were
// stale within a day.) This is what makes the chat page's "audit of the
// property's own site" claim true rather than aspirational.
export const getKnowledgeGaps = (
  clientId,
  { status = 'open', minOccurrences = 1, limit = 5, modality = null } = {},
) =>
  conversationRpc('fn_get_knowledge_gaps',
    {
      p_client_id: clientId,
      p_status: status,
      p_min_occurrences: minOccurrences,
      p_limit: limit,
      p_modality: modality,
    },
    'knowledge gaps');


// fn_get_conversation_list — 13 filters + {p_page, p_limit}. Server-paginated.
// Returns {items[], pagination{}}. Feeds: the list (Phase 7 — highest risk, R1).
// `filters` is a flat map of p_* filter params; callers pass only the ones set.
// Null/undefined values are dropped so we never send `null` params (some RPCs
// reject those with PGRST202).
export const getConversationList = (clientId, { page = 1, limit = 25, ...filters } = {}) => {
  const params = { p_client_id: clientId, p_page: page, p_limit: limit, ...filters };
  for (const k of Object.keys(params)) {
    if (params[k] == null) delete params[k];
  }
  return conversationRpc('fn_get_conversation_list', params, 'conversation list');
};

// Intelligence fields for the list, keyed by conversation_id.
//
// The list itself still loads through `getTranscripts`, which reads the
// `conversations` table and therefore carries none of the derived intelligence
// the row content and export columns are specified against — outcome,
// stay_type_sought, booking_urgency, conversion_barriers, estimated_lost_value,
// has_recording, caller_ever_spoke, requires_followup. Those exist only on
// fn_get_conversation_list.
//
// Enriching rather than migrating keeps search, Tags, sort, Clear, CSV, PDF and
// the total counter working over the full in-memory set — they all assume it —
// while still getting the fields. A row the RPC does not return simply keeps its
// existing render; the merge is additive and never removes a field.
//
// Pages until the reported total is covered, capped so a large property cannot
// spin forever. Returns {} on failure: enrichment is progressive, never a gate.
export const getConversationIntelMap = async (
  clientId,
  { modality = 'voice', startDate = null, endDate = null, limit = 500, maxPages = 4 } = {},
) => {
  const map = {};
  try {
    for (let page = 1; page <= maxPages; page += 1) {
      const params = { p_client_id: clientId, p_page: page, p_limit: limit };
      if (modality) params.p_modality = modality;
      // Send the date pair or neither — p_start_date alone times out server-side.
      if (startDate && endDate) { params.p_start_date = startDate; params.p_end_date = endDate; }
      // eslint-disable-next-line no-await-in-loop
      const data = await conversationRpc('fn_get_conversation_list', params, 'conversation list');
      const items = Array.isArray(data?.items) ? data.items : [];
      for (const it of items) {
        if (it?.conversation_id) map[it.conversation_id] = it;
      }
      const totalPages = Number(data?.pagination?.total_pages) || 1;
      if (items.length === 0 || page >= totalPages) break;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[ConversationIntel] list enrichment failed:', e.message);
    return map;
  }
  return map;
};

// fn_get_conversation_detail — {p_conversation_id} only.
// Returns {conversation, messages[], call} — `call` is the phone_call_sessions
// record for voice (null for chat / unlinked). NOTE: already wired in
// api/auth.js as getConversationMessages (which normalizes + re-sorts messages
// and the timestamp re-sort at api/auth.js:557–575 that fixes the repaired-
// transcript ordering bug — that re-sort MUST survive the Phase 8 rewrite).
// This raw wrapper is provided so the new detail pane can read the
// `conversation` and `call` objects the legacy helper discards.
export const getConversationDetail = (conversationId) =>
  conversationRpc('fn_get_conversation_detail',
    { p_conversation_id: conversationId },
    'conversation detail');

// fn_get_conversation_intelligence — {p_conversation_id} only.
// Returns {inquiry, outcome, signals, timing, provenance}. Feeds the detail pane
// (Phase 8), fired TOGETHER with fn_get_conversation_detail on row select.
export const getConversationIntelligence = (conversationId) =>
  conversationRpc('fn_get_conversation_intelligence',
    { p_conversation_id: conversationId },
    'conversation intelligence');

// fn_get_person_profile — {p_client_id, p_person_id}.
// Returns {relationship, relationship_current, facts[], person_id,
// cross_property_included}. Feeds the guest card that links out to Guest
// Intelligence (Phase 8 detail pane).
//
// The second parameter is p_client_id, not p_include_cross_property — the
// deployed function scopes by client and reports whether cross-property data was
// included in its own response.
//
// Build Spec v2 §7.2 says the second parameter is p_include_cross_property. IT IS
// WRONG, and the call below is deliberately NOT changed to match it.
//
// EVIDENCE: a live probe on 2026-08-28 against Verde Ranch as a platform admin,
// in which p_include_cross_property returned PGRST202 (no matching overload)
// while p_client_id returned 200. First observed the same way on 2026-08-24 and
// unchanged between the two. Both are observations of the function AS DEPLOYED
// ON THOSE DATES, not a standing guarantee — if the deployed overload set
// changes, this call is what has to change with it, and re-probing is the only
// way to know. A spec sentence does not outrank a live probe; "fixing" this to
// match the document would 404 every guest card on the page.
export const getPersonProfile = (clientId, personId) =>
  conversationRpc('fn_get_person_profile',
    { p_client_id: clientId, p_person_id: personId },
    'person profile');


// fn_get_conversation_topics — {p_client_id, p_start_date, p_end_date,
// p_modality, p_limit}. CHAT ONLY: no other page reads it, so a shape change
// surfaces here first. Returns {period, modality, topics[], questions[],
// conversations}:
//   topics[]    {topic, count, pct_of_conversations}
//   questions[] {question, count}
// Feeds: chat_questions (Tier 3) — questions verbatim ranked by frequency, with
// topics[] as clickable bars that filter the list. Verified live 2026-08-26.
export const getConversationTopics = (clientId, startDate, endDate, limit = 25) =>
  conversationRpc('fn_get_conversation_topics',
    {
      p_client_id: clientId,
      p_start_date: startDate,
      p_end_date: endDate,
      p_modality: 'chat',
      p_limit: limit,
    },
    'conversation topics');

// fn_drill — {p_what, p_id, p_scope, p_limit}. The one drill RPC (V6 A.5.2):
// every clickable element on the page resolves through it. Returns
// {kind, rows[], label} where each row carries conversation_id, modality,
// started_at, duration_seconds, name, phone, email, person_id, outcome,
// stay_type_sought, value, lost_value, has_recording, summary.
//
//   what  — one of the THIRTEEN p_what values (A.3 + Addendum 1 §4): stage,
//           barrier, stay_type, source, topic, outcome, after_hours, recoverable,
//           unreachable, followup, never_spoke, conversation, reservation.
//   id    — the p_id for that kind (a stage/barrier/source/topic/outcome key, a
//           conversation_id, or NULL for the whole-population kinds: after_hours,
//           recoverable, unreachable, followup, never_spoke). For 'reservation'
//           the id is one of exactly three values: 'paid', 'unpaid', or NULL (both).
//   scope — the page's LIVE filters: {client_id, start_date, end_date, modality}.
//           A drill from a filtered page keeps that filter. Pass exactly what the
//           page is currently showing (spec: "p_scope carries the page's live
//           filters").
//   limit — row cap, default 100.
//
// RESERVATION ROWS HAVE A DIFFERENT SHAPE — they come from agent_reservations, not
// conversation_intelligence, so the result carries kind:"reservation" and each row
// is {conversation_id, reservation_id, channel, booked_at, guest_name, phone,
// email, check_in, check_out, nights, adults, children, pets, site_name,
// site_number, rig, total, nightly_rate, status, paid, referral_source,
// checkout_url}. A conversation row and a reservation row are not interchangeable:
// the DrillOver body must branch on `kind`. checkout_url is null until payment, so
// it is null on every 'unpaid' row (a known backend gap) — render the link only
// when present, never a dead one.
//
// The DrillOver body renders conversation rows via search-console/PaginatedTable;
// reservation rows get the reservation renderer in ConversationPage's DrillSet.
export const drill = (what, id, scope, limit = 100) =>
  conversationRpc('fn_drill',
    { p_what: what, p_id: id, p_scope: scope, p_limit: limit },
    `drill ${what}`);
